import { ClipboardCheck, Search, CalendarCheck, ShieldCheck } from "lucide-react";
import { site } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { EstimateForm } from "@/components/EstimateForm";

const steps = [
  { icon: ClipboardCheck, title: "We review your request", text: "Every submission is read by a real person — no automated quotes." },
  { icon: Search, title: "We discuss the details", text: "We reach out by your preferred method, and visit on-site when the project calls for it." },
  { icon: CalendarCheck, title: "You get a written estimate", text: "Clear scope, preparation and pricing in writing before any work is booked." },
];

export default function Estimate() {
  return (
    <>
      <SEO
        title="Request a Free Estimate | North Ember Painting — Delta, BC"
        description="Request a free painting estimate from North Ember Painting. Interior, exterior, cabinet and commercial painting in Delta, Ladner, Tsawwassen and Surrey, BC."
        path="/estimate"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Request an Estimate", path: "/estimate" }])]}
      />
      <PageHero
        eyebrow="Free estimate"
        title="Tell us about your project"
        intro="A few details help us understand your project before we talk. No obligation, no pressure — and no vague pricing games."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-3">
            <Reveal className="lg:col-span-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-lg sm:p-10">
                <EstimateForm />
              </div>
            </Reveal>
            <aside>
              <Reveal delay={100}>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                  <h2 className="font-display text-xl font-semibold text-slate-900">What happens next</h2>
                  <ul className="mt-5 space-y-5">
                    {steps.map((s) => (
                      <li key={s.title} className="flex items-start gap-4">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ember/10 text-ember">
                          <s.icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">{s.title}</h3>
                          <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.text}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={160}>
                <div className="mt-6 rounded-2xl bg-ink p-7 text-slate-300">
                  <ShieldCheck className="h-6 w-6 text-ember" aria-hidden="true" />
                  <p className="mt-3 text-sm leading-relaxed">
                    Fully insured · {site.yearsExperience} years of experience · Serving Delta, Ladner,
                    Tsawwassen & Surrey
                  </p>
                  <p className="mt-4 border-t border-white/10 pt-4 text-sm">
                    Prefer to talk?{" "}
                    <a href={site.phoneHref} data-testid="estimate-sidebar-phone" className="font-semibold text-ember">
                      {site.phone}
                    </a>
                    <br />
                    {site.hours}
                  </p>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
